import { store } from "../store";
import { CreateGroupParams, Group } from "../types/groups.types";
import { api } from "../http";
import { DefaultRes } from "../types";
import { AxiosError, AxiosResponse } from "axios";
import { RESPONSE_CODES } from "../constants/responseCodes.constants";
import { TeamService } from "./team.service";

export class GroupService {
    public static readonly dispatch = store.dispatch.groups; // 👈 Reference to groups store
    public static readonly state = store.getState();

    /**
     * Fetches all groups.
     * @param callback Optional callback for handling responses.
     * @returns The fetched groups.
     */
    public static async fetchAll(
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Group[] | void> {
        const {
            dispatch: { toggleLoading, setGroups },
        } = GroupService;

        toggleLoading("fetchAll", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<Group[]>,
                AxiosResponse<DefaultRes<Group[]>>
            >(`groups/fetch-all-groups-teams-users`);
            toggleLoading("fetchAll", false);

            if (isValid) {
                setGroups(result);
                const {
                    dispatch: { setTeams },
                } = TeamService;
                let teams: any[] = []
                result.forEach((g) => {
                    teams = [...teams, ...g.teams]
                })
                setTeams(teams);

            }
            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("fetchAll", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Group>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
 * Creates a new group by calling "groups/create-group" endpoint.
 * @param group The group object to be created.
 * @param callback Optional callback function for handling response.
 * @returns The newly created group if successful.
 */
    public static async createGroup(
        group: CreateGroupParams,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Group | void> {
        const {
            dispatch: { toggleLoading, setGroups },
        } = GroupService;

        toggleLoading("createGroup", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Group>,
                AxiosResponse<DefaultRes<Group>>
            >(`groups/create-group`, group);

            toggleLoading("createGroup", false);
            if (isValid) {
                const state = store.getState();
                const newItems = [...state.groups.items, result];

                setGroups(newItems);
                this.setGroup(result)

                // Set the newly created group as the selected group
                this.setGroup(result);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("createGroup", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Group>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Updates a group by calling "groups/update-group" endpoint.
     * @param group The group object to be updated.
     * @param callback Optional callback function for handling response.
     * @returns The updated group object if successful.
     */
    public static async updateGroup(
        group: Group,
        updates: CreateGroupParams,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Group | void> {
        const {
            dispatch: { toggleLoading, updateGroupInStore },
        } = GroupService;

        toggleLoading("updateGroup", true);
        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Group>,
                AxiosResponse<DefaultRes<Group>>
            >(`groups/update-group/${group.id!}`, updates);

            toggleLoading("updateGroup", false);

            if (isValid) {
                updateGroupInStore(result);

                // Set the updated group as the selected group
                this.setGroup(result);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("updateGroup", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Group>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
 * Deletes a group by calling the "groups/delete-group" endpoint.
 * @param groupId The ID of the group to be deleted.
 * @param callback Optional callback function for handling response.
 * @returns The deleted group object if successful.
 */
    public static async deleteGroup(
        groupId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Group | void> {
        const {
            dispatch: { toggleLoading, setGroups },
        } = GroupService;

        toggleLoading("deleteGroup", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Group>,
                AxiosResponse<DefaultRes<Group>>
            >(`groups/delete-group/${groupId}`);

            toggleLoading("deleteGroup", false);

            if (isValid) {
                const state = store.getState();
                const filtered = state.groups.items.filter((g: Group) => g.id !== groupId);
                setGroups(filtered);
            }
            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("deleteGroup", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Group>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Fetches a single group by ID.
     * @param groupId The ID of the group to fetch.
     * @param callback Optional callback for handling responses.
     * @returns The fetched group.
     */
    public static async fetchGroupById(
        groupId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Group | void> {
        const {
            dispatch: { toggleLoading, updateGroupInStore },
        } = GroupService;

        toggleLoading("fetchAll", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Group>,
                AxiosResponse<DefaultRes<Group>>
            >(`groups/get-group/${groupId}`);

            toggleLoading("fetchAll", false);

            if (isValid) {
                // Update the group in the store if it exists, otherwise add it
                const state = store.getState();
                const existingGroupIndex = state.groups.items.findIndex(g => g.id === groupId);

                if (existingGroupIndex >= 0) {
                    updateGroupInStore(result);
                } else {
                    const newItems = [...state.groups.items, result];
                    const { dispatch: { setGroups } } = GroupService;
                    setGroups(newItems);
                }

                // Set as selected group
                this.setGroup(result);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("fetchAll", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Group>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Sets the selected group in the store.
     * @param group The group object to set.
     */
    public static setGroup(group: Group | null) {
        const {
            dispatch: { setSelectedGroup },
        } = GroupService;
        if (group != null) {
            const newGroup: Group = structuredClone(group);
            setSelectedGroup(newGroup);
        } else {
            setSelectedGroup(null)
        }

    }
}
